import React from 'react';
('use client');

import { useEffect } from 'react';
import ContextDetector from './lib/context-detection-engine';
import { useConfigStore, useContextStore } from '@/hooks';
import { getHistoryPatcher } from './lib/history-patcher';

export interface ContextEngineProps {
  instanceId: string;
}

export const ContextEngine: React.FC<ContextEngineProps> = ({ instanceId }) => {
  const { setPageContext, setCustomContext } = useContextStore();
  const config = useConfigStore();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const detector = new ContextDetector({
      analytics: {
        log: (_event, _payload) => {
          // Analytics logging disabled
        },
      },
      cacheTTL: 5 * 60 * 1000,
      pageTypes: config.context,
      disableCache: false,
    });

    let lastUrl = window.location.href;
    let debounceTimer: number | null = null;

    const runDetection = async () => {
      const href = window.location.href;
      if (href === lastUrl) return;
      lastUrl = href;
      const ctx = await detector.detect({ url: href, doc: document });
      setPageContext(ctx.context);
      setCustomContext(ctx.context.extracted || {});
      ctx.onMatch(ctx.context);
    };

    const debounceDetect = () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(runDetection, 200);
    };

    // Use global history patcher singleton instead of patching directly
    // This ensures history API is only patched once, regardless of instance count
    const historyPatcher = getHistoryPatcher();
    historyPatcher.register(instanceId, debounceDetect);

    // Still listen for browser events (popstate, hashchange)
    const onPopState = () => debounceDetect();
    const onHashChange = () => debounceDetect();

    window.addEventListener('popstate', onPopState);
    window.addEventListener('hashchange', onHashChange);

    // initial detection
    (async () => {
      const ctx = await detector.detect({ url: lastUrl, doc: document });
      setPageContext(ctx.context);
      setCustomContext(ctx.context.extracted || {});
      ctx.onMatch(ctx.context);
    })();

    return () => {
      // Unregister from global history patcher
      historyPatcher.unregister(instanceId);

      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('hashchange', onHashChange);

      if (debounceTimer) window.clearTimeout(debounceTimer);
    };
  }, [instanceId]);

  return null;
};
